import { useId, type ReactNode } from 'react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import type { LinkTarget } from '@utils/embeds/embedConfig'
import type { PublicEvent } from '@utils/embeds/publicEvent'
import { EmbedLink } from './EmbedButton'

const t = scopedTranslation('embeds.events')

const TEXT_LINK_FOCUS =
  'tw:focus-visible:rounded-xs tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-[color:var(--embed-brand-ink,#111827)]'

function linkTarget(links: LinkTarget) {
  return links === 'new' ? { target: '_blank', rel: 'noopener' } : { target: '_top' }
}

function ScreenReaderEventName({ event, links }: { event: Pick<PublicEvent, 'name'>; links: LinkTarget }) {
  return (
    <span className="tw:sr-only">
      {links === 'new' ? `: ${event.name} ${t('opens_in_new_tab')}` : `: ${event.name}`}
    </span>
  )
}

type EventPageLinkProps = {
  event: Pick<PublicEvent, 'name' | 'url'>
  links: LinkTarget
  rsvpLabel: string
  full: boolean
}

export function EventPageLink({ event, links, rsvpLabel, full }: EventPageLinkProps) {
  if (!event.url) return null

  return (
    <EmbedLink href={event.url} {...linkTarget(links)} outline={full}>
      {full ? t('view_event') : rsvpLabel}
      <ScreenReaderEventName event={event} links={links} />
      {links === 'new' && <Icon type="external-link" className="tw:h-3.5 tw:w-3.5" />}
    </EmbedLink>
  )
}

type EventTitleLinkProps = {
  event: Pick<PublicEvent, 'url'>
  links: LinkTarget
  children: ReactNode
}

export function EventTitleLink({ event, links, children }: EventTitleLinkProps) {
  const hintId = useId()
  if (!event.url) return children

  return (
    <a
      href={event.url}
      {...linkTarget(links)}
      aria-describedby={links === 'new' ? hintId : undefined}
      className={cn('tw:block tw:text-inherit tw:no-underline tw:hover:underline', TEXT_LINK_FOCUS)}
    >
      {children}
      {links === 'new' && (
        <span id={hintId} hidden>
          {t('opens_in_new_tab')}
        </span>
      )}
    </a>
  )
}

type EventDateLinkProps = {
  event: Pick<PublicEvent, 'name' | 'url'>
  links: LinkTarget
  children: ReactNode
}

export function EventDateLink({ event, links, children }: EventDateLinkProps) {
  if (!event.url) return children

  return (
    <a
      href={event.url}
      {...linkTarget(links)}
      className={cn('tw:text-gray-700 tw:underline tw:underline-offset-2 tw:hover:text-gray-900', TEXT_LINK_FOCUS)}
    >
      {children}
      <ScreenReaderEventName event={event} links={links} />
    </a>
  )
}
